import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Menu, Moon, Sun } from 'lucide-react';
import { FaktLogo } from '@/components/brand';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { usePersistedToggle } from '@/lib/use-persisted-toggle';
import { useRecentPages } from '@/lib/use-recent-pages';
import { useTheme } from '@/lib/theme-context';
import { cn, initials } from '@/lib/utils';
import { AppSidebarGroup } from './app-sidebar-group';
import {
  applyNavBadges,
  buildSidebarGroups,
  groupHeaderForPath,
  navItemForPath,
  GROUP_DEFAULT_OPEN,
  type SidebarGroup,
} from './app-sidebar-groups';
import type { NavItem } from './layout';

/** BL-285: eigener Gruppen-Header für den "Zuletzt besucht"-Verlauf -- kein
 *  `NavItem.section`-Wert, deshalb außerhalb von `GROUP_CONFIG`. */
const RECENT_PAGES_HEADER = 'ZULETZT BESUCHT';

interface NavBadgeCounts {
  rechnungenGesamt: number;
  mahnwesenUeberfaellig: number;
}

const GROUPS_STORAGE_KEY = 'fakt-nav-groups-v1';

// BL-285: "Zuletzt besucht" per Default aufgeklappt -- ist der ganze Zweck
// der Gruppe (schneller Rücksprung), eingeklappt hinter einem Klick würde
// den Vorteil gegenüber der Browser-Historie zunichtemachen.
const OPEN_GROUP_DEFAULTS: Record<string, boolean> = {
  ...GROUP_DEFAULT_OPEN,
  [RECENT_PAGES_HEADER]: true,
};

function loadOpenGroups(): Record<string, boolean> {
  if (typeof window === 'undefined') return OPEN_GROUP_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(GROUPS_STORAGE_KEY);
    if (!raw) return OPEN_GROUP_DEFAULTS;
    return { ...OPEN_GROUP_DEFAULTS, ...(JSON.parse(raw) as Record<string, boolean>) };
  } catch {
    return OPEN_GROUP_DEFAULTS;
  }
}

/**
 * BL-297: löst die BL-284-Icon-Schiene + separates Bereichs-Panel ab -- eine
 * einzige klassische Sidebar mit kollabierbaren Gruppen statt zwei
 * Navigations-Ebenen (direktes User-Feedback am Live-Stand, gleicher Tag:
 * "zu viele Klicks, zu viel Platz"). Ganze Sidebar bleibt global einklappbar
 * (Hamburger-Icon, Icon-only-Rail wenn eingeklappt -- derselbe
 * `usePersistedToggle`-Key wie zuvor, behält die bestehende User-Präferenz),
 * einzelne Gruppen unabhängig davon auf/zu (siehe `app-sidebar-groups.ts`).
 *
 * "Business Units" aus dem User-Mockup als eingerücktes Untermenü unter
 * Stammdaten fehlt bewusst -- das Konzept wurde am selben Tag (BL-273) komplett
 * entfernt, die Skizze war vor diesem Merge entstanden. Nur "Rollen" bleibt,
 * flach statt verschachtelt (ein einzelner Punkt braucht kein Untermenü).
 */
export function AppSidebar({
  sections,
}: {
  sections: Array<{ section: string; items: NavItem[] }>;
}) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [navCollapsed, setNavCollapsed] = usePersistedToggle('fakt-nav-collapsed', false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);

  // BL-298: Zähler-Badges für "Rechnungen"/"Mahnwesen" -- reine Navigations-
  // zahlen (kein Gehalt/Detail), deshalb ohne Rollen-Einschränkung ladbar.
  // 60s staleTime, weil die Sidebar auf jeder Seite gemountet ist -- ein
  // kompletter Neu-Fetch bei jedem Routenwechsel wäre unnötig teuer für
  // Zahlen, die sich innerhalb einer Session selten sichtbar ändern.
  const badgesQuery = useQuery({
    queryKey: ['dashboard', 'nav-badges'],
    queryFn: async () => (await api.get<NavBadgeCounts>('/dashboard/nav-badges')).data,
    staleTime: 60_000,
  });
  const navBadges: Record<string, { value: string; tone?: 'default' | 'danger' }> = badgesQuery.data
    ? {
        '/orderbuch': { value: String(badgesQuery.data.rechnungenGesamt) },
        '/mahnwesen': {
          value: String(badgesQuery.data.mahnwesenUeberfaellig),
          tone: badgesQuery.data.mahnwesenUeberfaellig > 0 ? 'danger' : 'default',
        },
      }
    : {};

  const allItems = applyNavBadges(
    sections.flatMap((s) => s.items),
    navBadges,
  );
  const { start, groups } = buildSidebarGroups(allItems);

  // BL-285: "Zuletzt besucht" -- MRU-Verlauf unabhängig von Sidebar-Gruppen
  // (siehe use-recent-pages.ts), oben in `groups` vorangestellt statt über
  // buildSidebarGroups zu laufen (das gruppiert nach NavItem.section, hier
  // zählt Besuchsreihenfolge). Aktuelle Seite wird aus der ANZEIGE gefiltert
  // (sie ist ohnehin da, kein Rücksprung nötig) -- bleibt aber im
  // gespeicherten Verlauf, damit sie beim nächsten Wechsel wieder vorn steht.
  const currentTo = navItemForPath(allItems, location.pathname)?.to;
  const recentTos = useRecentPages(currentTo);
  const recentItems = recentTos
    .filter((to) => to !== currentTo)
    .map((to) => allItems.find((i) => i.to === to))
    .filter((i): i is NavItem => i !== undefined);
  const recentGroup: SidebarGroup | undefined =
    recentItems.length > 0
      ? { header: RECENT_PAGES_HEADER, items: recentItems, chipItems: [], defaultOpen: true }
      : undefined;
  const allGroups = recentGroup ? [recentGroup, ...groups] : groups;

  // Navigiert der User direkt (Deep-Link, Suche, Browser-Zurück) in eine
  // per Default eingeklappte Gruppe (z.B. "System"), wird sie automatisch
  // aufgeklappt -- sonst wäre die aktive Seite in einer unsichtbaren Gruppe
  // "versteckt". Überschreibt nur ein spezifisches "geschlossen" für DIESE
  // Gruppe, ein manuelles Zuklappen danach bleibt möglich (kein Kampf gegen
  // den User bei jedem Reload derselben Route, da die Persistenz separat
  // greift -- dieser Effekt schreibt nur den In-Memory-State, nicht localStorage).
  useEffect(() => {
    const activeHeader = groupHeaderForPath(groups, location.pathname);
    if (activeHeader && !openGroups[activeHeader]) {
      setOpenGroups((prev) => ({ ...prev, [activeHeader]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei Routenwechsel neu prüfen, nicht bei jedem openGroups-Update
  }, [location.pathname]);

  const toggleGroup = (header: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [header]: !prev[header] };
      try {
        window.localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* Storage nicht verfügbar -- In-Memory-State bleibt für die Session gültig */
      }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const roleLabel =
    user?.role === 'ADMIN' ? 'Admin' : user?.role === 'USER' ? 'Buchhalter' : 'Leser';

  // Eingeklappt: schlanke Icon-only-Rail, jedes Item (inkl. Chip-Einträge)
  // als eigenes Icon, EIN flaches Level (kein zweites Panel mehr) -- dünne
  // Trennlinien zwischen Gruppen statt Text-Headern, dafür ist kein Platz.
  if (navCollapsed) {
    return (
      <aside
        className="hidden w-[52px] shrink-0 flex-col items-center border-r border-hair bg-panel md:sticky md:top-0 md:flex md:h-screen"
        aria-label="Navigation"
      >
        <NavLink to="/" className="flex items-center justify-center py-4" title="FAKT — Übersicht">
          <FaktLogo size={20} />
        </NavLink>
        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-1">
          {start && (
            <NavLink
              to={start.to}
              end
              title={start.label}
              aria-label={start.label}
              className={({ isActive }) =>
                cn(
                  'flex h-9 w-9 items-center justify-center',
                  isActive
                    ? 'bg-avameo-primary/10 text-avameo-primary'
                    : 'text-ink-soft hover:bg-bg-alt hover:text-ink',
                )
              }
            >
              <start.icon className="h-[18px] w-[18px]" />
            </NavLink>
          )}
          {allGroups.map((group) => (
            <div
              key={group.header}
              className="flex flex-col items-center gap-1 border-t border-hair pt-1"
            >
              {[...group.items, ...group.chipItems].map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  aria-label={label}
                  className={({ isActive }) =>
                    cn(
                      'flex h-9 w-9 items-center justify-center',
                      isActive
                        ? 'bg-avameo-primary/10 text-avameo-primary'
                        : 'text-ink-soft hover:bg-bg-alt hover:text-ink',
                    )
                  }
                >
                  <Icon className="h-[18px] w-[18px]" />
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="flex flex-col items-center gap-1 border-t border-hair py-2">
          <button
            type="button"
            onClick={() => setNavCollapsed(false)}
            aria-expanded={false}
            aria-label="Navigation ausklappen"
            title="Navigation ausklappen"
            className="flex h-9 w-9 items-center justify-center text-ink-soft hover:bg-bg-alt hover:text-ink"
          >
            <Menu className="h-4 w-4" />
          </button>
          <NavLink
            to="/konto"
            title={`${user?.name ?? '—'} · ${roleLabel} — Mein Konto`}
            className={({ isActive }) =>
              cn(
                'flex h-9 w-9 items-center justify-center rounded-full',
                isActive && 'ring-2 ring-avameo-primary ring-offset-1 ring-offset-panel',
              )
            }
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-avameo-primary text-[10px] font-semibold text-white">
              {initials(user?.name)}
            </span>
          </NavLink>
          <button
            type="button"
            onClick={handleLogout}
            title="Abmelden"
            aria-label="Abmelden"
            className="flex h-9 w-9 items-center justify-center text-ink-soft hover:bg-bg-alt hover:text-ink"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="hidden w-[232px] shrink-0 flex-col border-r border-hair bg-panel md:sticky md:top-0 md:flex md:h-screen"
      aria-label="Navigation"
    >
      <div className="flex items-center justify-between px-3 py-4">
        <NavLink to="/" className="flex items-center gap-2" title="FAKT — Übersicht">
          <FaktLogo size={20} />
        </NavLink>
        <button
          type="button"
          onClick={() => setNavCollapsed(true)}
          aria-expanded={true}
          aria-label="Navigation einklappen"
          title="Navigation einklappen"
          className="flex h-8 w-8 items-center justify-center text-ink-soft hover:bg-bg-alt hover:text-ink"
        >
          <Menu className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto pb-2">
        {start && (
          <NavLink
            to={start.to}
            end
            className={({ isActive }) =>
              cn(
                'mx-3 mb-1 flex items-center gap-3 px-2.5 py-[7px] text-[13px] transition-colors',
                'border-l-2',
                isActive
                  ? 'border-avameo-primary bg-avameo-primary/5 text-avameo-primary'
                  : 'border-transparent text-ink-soft hover:bg-bg-alt hover:text-ink',
              )
            }
          >
            <start.icon className="h-[16px] w-[16px] shrink-0" />
            {start.label}
          </NavLink>
        )}

        {allGroups.map((group) => (
          <AppSidebarGroup
            key={group.header}
            group={group}
            open={openGroups[group.header] ?? true}
            onToggle={() => toggleGroup(group.header)}
          />
        ))}
      </nav>

      <div className="flex items-center gap-2 border-t border-hair px-3 py-2.5">
        <NavLink
          to="/konto"
          className={({ isActive }) =>
            cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              isActive && 'ring-2 ring-avameo-primary ring-offset-1 ring-offset-panel',
            )
          }
          title="Mein Konto"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-avameo-primary text-[10px] font-semibold text-white">
            {initials(user?.name)}
          </span>
        </NavLink>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-medium text-ink">{user?.name ?? '—'}</div>
          <div className="truncate text-[11px] text-ink-muted">{roleLabel}</div>
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
          aria-label={theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
          className="flex h-8 w-8 shrink-0 items-center justify-center text-ink-soft hover:bg-bg-alt hover:text-ink"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={handleLogout}
          title="Abmelden"
          aria-label="Abmelden"
          className="flex h-8 w-8 shrink-0 items-center justify-center text-ink-soft hover:bg-bg-alt hover:text-ink"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
